import { defineStore } from 'pinia';
import type {} from 'pinia-plugin-persistedstate';
import { readCookie } from '@/modules/cookies';
import { usePreferenceStore } from '@/stores/preference';
import { configureDangerSignConditions } from '@shared/clinical-logic/helpers/dangerSigns';

export interface StreamlinedOption {
  id: string;
  name: string;
  value: string;
  ordering: number;
  selected: boolean;
  preselect: boolean;
}

export type AssessmentType = 'focused' | 'comprehensive';

export interface TrackState {
  all: any[];
  current: any;
  selected: string | number | null;
  comprehensive: boolean;
  initialAssessmentType?: AssessmentType | null;
  streamlinedOptions: StreamlinedOption[];
  thresholdSet: string | null;
  filterConditions: string[];
  engagementKey: string | null;
}

// V6 reads tracks_diseases too, so results only V7 runs are listed here, not in the database.
const V7_ONLY_CONDITIONS: { name: string, patients: string[] }[] = [
  { name: 'eye_abnormality', patients: ['newborn', 'child', 'youth', 'adult'] },
  { name: 'eye_trauma', patients: ['child', 'youth', 'adult'] },
];

function addV7OnlyConditions(conditions: any[], patient: string) {
  const configuredNames = new Set(conditions.map(condition => condition.name));
  const additional = V7_ONLY_CONDITIONS
    .filter(({ name, patients }) => patients.includes(patient) && !configuredNames.has(name))
    .map(({ name }) => ({ name, plans: [] }));
  return additional.length ? [...conditions, ...additional] : conditions;
}

export function configuredConditionsForTrack(track: TrackState['current']) {
  const patient = track?.classifications?.patient || track?.path;
  let diseases = track?.diseases || [];
  if (track?.type === 'clinical') diseases = addV7OnlyConditions(diseases, patient);
  if (track?.flexiDangerSigns !== true) return diseases;
  return configureDangerSignConditions(diseases, patient);
}

const useLanguageStore = () => require('@/stores/language').useLanguageStore();
const gatherResponses = (track: any) => require('@/utils/panelResponses').gatherResponses(track);
const mrxStreamlinedOptions = (bundle: any[]) => require('@/utils/mrxEngine').streamlinedOptions(bundle);
const mrxStreamlinedEvaluation = (payload: any) => require('@/utils/mrxEngine').streamlinedEvaluation(payload);

export const defaultState = (): TrackState => ({
  all: [],
  current: {},
  selected: null,
  comprehensive: true,
  initialAssessmentType: null,
  streamlinedOptions: [],
  thresholdSet: null,
  filterConditions: [],
  engagementKey: null,
});

export const useTrackStore = defineStore('track', {
  persist: true,
  state: defaultState,

  getters: {
    active(state): boolean {
      return state.selected !== null;
    },

    // This setting covers the new Flexi features, not the existing maternal danger signs.
    dangerSignsEnabled(state): boolean {
      return state.current?.flexiDangerSigns === true;
    },

    // The care plan override for this track, if the engagement configures one.
    carePlanOverrides(state): any | null {
      return state.current?.flexiCarePlans ?? null;
    },

    isComprehensiveScope(state): boolean {
      return state.initialAssessmentType !== null
        && state.initialAssessmentType !== undefined
        && state.comprehensive;
    },

    responseTokens(): Record<string, string> {
      const results: Record<string, string> = {};

      const languageStore = useLanguageStore();
      let allPanels = languageStore.responses;
      allPanels = allPanels.concat(usePreferenceStore().all);

      allPanels.forEach((panel: any) => {
        panel.responses.forEach((response: any) => {

          let token;

          if (panel.options?.confirmValueInSummary) {
            token = `generic.confirm`;
          }
          else if (panel.type === 'lookup') {
            token = `${response.name}.${response.value}`;
          }
          else if (panel.options?.overrideGenericToken?.includes(response.value)) {
            token = response.name;
          }
          else if (['yes', 'no', 'unknown'].includes(response.value)) {
            token = `generic.${response.value}`;
          }
          else if (panel.type === 'multiple' && !panel.options?.getResponsesFromTokens) {
            token = response.value;
          }
          else {
            token = response.name;
          }

          results[response.name] = `responses.${token}.title`;

        });
      });

      return results;
    },

    conditions(state): any[] {
      let results: any[] = [];
      try {
        const diseases = configuredConditionsForTrack(state.current);
        results = diseases.map((condition: any) => {
          return {
            type: 'condition',
            ...condition
          };
        });
      } catch (e) {}
      return results;
    },

    classifications(state): any[] {
      let results: any[] = [];
      try {
        const classifications: Record<string, any> = {
          patient: state.current.classifications?.patient || state.current.path,
          audience: state.current.classifications?.audience || state.current.target_audience,
          experience: state.current.classifications?.experience || 'pwa',
        };

        results = Object.keys(classifications).map(key => ({
          type: 'classification',
          name: key,
          value: classifications[key],
        }));
      } catch (e) {}

      return results;
    },

    settings(): (name: string) => any {
      return (name: string) => {
        let result;
        try {
          if (['audience', 'patient'].includes(name)) {
            result = this.classifications.find((c: any) => c.name === name).value;
          }

          const track = this.current;
          if (track.options?.name) {
            result = track.options[name];
          }
        } catch (e) {
          console.log(e);
        }
        return result;
      };
    },

    selectedStreamlinedOptions(state): string[] {
      return state.streamlinedOptions.filter(s => s.selected).map(s => s.value);
    },

    streamlinedOptionsDataSave(state): { name: string, status: string }[] {
      return state.streamlinedOptions.map(s => {
        let status = 'not_selected';
        if (s.preselect) {
          status = 'preselected';
        } else if (s.selected) {
          status = 'selected';
        }
        return { name: s.value, status };
      });
    },
  },

  actions: {
    reset(): void {
      const engagementKey = this.engagementKey;
      const all = this.all;
      this.$reset();
      this.$patch({ engagementKey, all });
    },

    setAll(tracks: any[]): void {
      this.all = tracks || [];
    },

    resume(payload: Partial<TrackState>): void {
      Object.assign(this, payload);
    },

    selectTrack(track: any): void {
      this.current = JSON.parse(JSON.stringify(track));
      this.selected = track.id;
    },

    setFilterConditions(payload: string[]): void {
      this.filterConditions = payload;
    },

    startComprehensiveAssessment(): void {
      if (this.initialAssessmentType !== 'focused') {
        this.initialAssessmentType = 'comprehensive';
      }
      this.comprehensive = true;
      this.streamlinedOptions = [];
      this.thresholdSet = null;
      this.filterConditions = [];
    },

    startFocusedAssessment(): void {
      if (this.initialAssessmentType !== null && this.initialAssessmentType !== undefined) return;
      this.initialAssessmentType = 'focused';
      this.comprehensive = false;
    },

    escalateToComprehensiveAssessment({ keepFocusedSelections = false }: { keepFocusedSelections?: boolean } = {}): void {
      if (this.initialAssessmentType === null || this.initialAssessmentType === undefined) {
        this.initialAssessmentType = 'focused';
      }
      this.comprehensive = true;
      this.filterConditions = [];
      // A consultation that started focused keeps the focused assessments it started, so the saved
      // encounter still records them next to its focused origin (TENG-851). They feed the encounter
      // details only, not the clinical logic.
      if (keepFocusedSelections) return;
      this.streamlinedOptions = [];
      this.thresholdSet = null;
    },

    returnToFocusedAssessment(): void {
      if (this.initialAssessmentType !== 'focused') return;
      this.comprehensive = false;
    },

    resetAssessmentState(): void {
      this.comprehensive = true;
      this.initialAssessmentType = null;
      this.streamlinedOptions = [];
      this.thresholdSet = null;
      this.filterConditions = [];
    },

    setPanelResponses(panel: any, responses: any[]): void {
      panel.responses = responses;
    },

    async initializeStreamlinedOptions(): Promise<void> {
      const classifications = this.classifications;
      const panelResponses = await gatherResponses(this.current);
      const bundle = [...classifications, ...panelResponses];
      const streamlinedOptions = await mrxStreamlinedOptions(bundle);

      this.streamlinedOptions = streamlinedOptions.map((tile: any, index: number) => {
        return {
          id: tile.name,
          name: `streamlined.${tile.name}`,
          value: tile.name,
          ordering: index * 100,
          selected: tile.preselect,
          preselect: tile.preselect,
        };
      });
    },

    async setStreamlinedTrack(
      payload: { selectedStreamlinedOptions?: string[], filterConditions?: string[] } = {},
    ): Promise<void> {
      let streamlinedSelections = this.selectedStreamlinedOptions;

      if (payload.selectedStreamlinedOptions) {
        streamlinedSelections = payload.selectedStreamlinedOptions;
      }

      let data: any = { comprehensive: true, thresholdSet: null };
      if (streamlinedSelections.length > 0) {
        const classifications = this.classifications.reduce((acc: Record<string, any>, cur: any) => {
          acc[cur.name] = cur.value;
          return acc;
        }, {});
        data = await mrxStreamlinedEvaluation({ classifications, streamlinedSelections });
      }

      if (payload.filterConditions) {
        data = { ...data, filterConditions: payload.filterConditions };
      }

      this.applyStreamlinedTrack(data);
    },

    applyStreamlinedTrack(payload: any): void {
      const filterConditions = payload.filterConditions || payload.conditions;

      if (this.initialAssessmentType === 'focused' && this.comprehensive && !payload.comprehensive) {
        return;
      }

      if (this.initialAssessmentType === null || this.initialAssessmentType === undefined) {
        this.initialAssessmentType = payload.comprehensive ? 'comprehensive' : 'focused';
      }
      this.comprehensive = payload.comprehensive;

      if (payload.comprehensive) {
        this.thresholdSet = payload.thresholdSet;
        this.filterConditions = filterConditions || [];
        return;
      }

      this.thresholdSet = null;
      this.filterConditions = filterConditions;
    },

    syncEngagement(): void {
      const key = readCookie('session');
      if (this.engagementKey === key) return;

      this.$reset();
      this.$patch({ engagementKey: key });
    },
  },
})
